import { SystemEntity } from "@erp/shared-types";
import type { SocketType } from "../../types";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
  supportsSocketIdV2,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { configEntryList, prettify } from "./socketDefinition";

export interface ResolvedIdentifyPartyOutputSockets {
  sockets: DynamicSocketDefinition[];
  /** Output socket id for each configured route id. */
  socketIdByRouteId: Map<string, string>;
}

/**
 * Shared generator for identify_customer / identify_supplier dynamic outputs.
 *
 * Always emits `top_pick`. Adds one entity-ref socket per configured route,
 * plus optional `others`.
 */
export function resolveIdentifyPartyOutputSockets(opts: {
  stepType: string;
  reserved: readonly string[];
  config: Record<string, unknown>;
  routesConfigKey: string;
  entityType: SystemEntity;
  entityNoun: string;
}): ResolvedIdentifyPartyOutputSockets {
  const {
    stepType,
    reserved,
    config,
    routesConfigKey,
    entityType,
    entityNoun,
  } = opts;

  const routes = configEntryList<{ id?: unknown; label?: unknown }>(
    config[routesConfigKey],
  );
  const enableOthers = config.enableOthersRoute === true;
  const othersNullOnNoMatch = config.othersEmitNullWhenNoMatch === true;
  const entityRefType: SocketType =
    `entity_reference:${entityType}` as SocketType;

  const sockets: DynamicSocketDefinition[] = [
    {
      id: "top_pick",
      name: "Top Pick",
      type: "json" as SocketType,
      required: false,
      description: `Top ${entityNoun} match with confidence (not-taken sentinel if no match meets threshold)`,
    },
  ];

  const usableRoutes = routes.filter(
    (route): route is { id: string; label?: unknown } =>
      typeof route.id === "string" && /^[a-z][a-z0-9_]*$/.test(route.id),
  );

  const { sockets: routeSockets, assignedIds } = buildDynamicSockets({
    reserved,
    version: effectiveSocketIdVersion(stepType, config),
    markedFallback: supportsSocketIdV2(stepType),
    entries: usableRoutes.map((route) => ({
      name: route.id,
      socket: {
        name:
          typeof route.label === "string" && route.label.length > 0
            ? route.label
            : prettify(route.id),
        type: entityRefType,
        required: false,
        description: `${entityNoun} ref when the match routes to "${route.id}"`,
      },
    })),
  });
  sockets.push(...routeSockets);

  if (enableOthers) {
    sockets.push({
      id: "others",
      name: "Others",
      type: entityRefType,
      required: false,
      description: othersNullOnNoMatch
        ? `${entityNoun} ref when matched but not on a named route; null when nothing matched (downstream still runs)`
        : `${entityNoun} ref when matched but does not match any configured route`,
    });
  }

  const socketIdByRouteId = new Map<string, string>();
  usableRoutes.forEach((route, index) => {
    const id = assignedIds[index];
    if (id !== null && id !== undefined) socketIdByRouteId.set(route.id, id);
  });

  return { sockets, socketIdByRouteId };
}

export function generateIdentifyPartyOutputSockets(
  opts: Parameters<typeof resolveIdentifyPartyOutputSockets>[0],
): DynamicSocketDefinition[] {
  return resolveIdentifyPartyOutputSockets(opts).sockets;
}
